import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { ChartDO, MenuDTO, SystemDTO } from './system-dtos';
import { MenuUtils } from '../common/utils/menu-utils';
import { AdminContextService } from '../common/services/admin.context.service';
import { addPromoClaimRateToMarketingConfig } from '../common/utils/promo-claim-rate-config.utils';

@Injectable()
export class SystemService {
  private cachedSystem: SystemDTO;

  private items: MenuDTO[];

  private selectAllUrlCodeMap: Map<string, string>;

  public systemTimeZoneSubject: BehaviorSubject<string> = new BehaviorSubject('UTC');

  constructor(
    private http: HttpClient,
    private adminContextService: AdminContextService
  ) {}

  public getMenuConfig(code: string): Observable<MenuDTO> {
    const theCode = code.replace('ticker', '');
    return this.getItems().pipe(map((items) => items.find((item) => item.code === theCode)));
  }

  public getCodeBySelectAllUrl(url: string): string {
    return this.selectAllUrlCodeMap ? this.selectAllUrlCodeMap.get(url) : null;
  }

  public getSystem(): Observable<SystemDTO> {
    return this.cachedSystem
      ? of(this.cachedSystem)
      : this.http
          .get<SystemDTO>('/form/system')
          .pipe(map((data) => addPromoClaimRateToMarketingConfig(data)))
          .pipe(tap((data) => (this.items = this.extractItems(data))))
          .pipe(tap((data) => this.constructMap(data)))
          .pipe(tap((data) => (this.cachedSystem = data)))
          .pipe(tap((data) => this.adminContextService.initialize(data.cohorts)));
  }

  private getItems(): Observable<MenuDTO[]> {
    return this.items ? of(this.items) : this.getSystem().pipe(map((_) => this.items));
  }

  private extractItems(system: SystemDTO): MenuDTO[] {
    return system.menuGroups.map((group) => group.menuItems).reduce((acc, elem) => acc.concat(elem), []);
  }

  private constructMap(system: SystemDTO): void {
    const tmpObj = {};

    this.items
      .filter((i) => {
        const firstMaintainConfig = MenuUtils.getFirstMaintainConfig(i.entityGroup);
        return firstMaintainConfig && firstMaintainConfig.tabs.length === 1;
      })
      .forEach((i) => {
        tmpObj[MenuUtils.getFirstMaintainConfig(i.entityGroup).tabs[0].endpoints.selectAllUrl] = i.code;
      });
    this.selectAllUrlCodeMap = new Map(Object.entries(tmpObj));
  }

  public getChart(chartName: string): Observable<ChartDO> {
    return (this.cachedSystem ? of(this.cachedSystem) : this.getSystem()).pipe(
      map((s: SystemDTO) => s.charts[chartName])
    );
  }
}
